import { useState } from "react";
import Button from "../../../../../components/fundamentals/button";
import CategoryPicker from "../../../category-mappings/CategoryPicker";
import useNotification from "../../../../../hooks/use-notification";
import productsService from "../../../../../services/products.service";
import { Category } from "../../../../../models/Category";

interface IProps {
  productIds: number[];
  count: number;
  onClear: () => void;
  onDone: () => void | Promise<void>;
}

/**
 * RAG-71 — Barre d'actions en masse de l'écran Produits : affecte une catégorie et/ou la
 * visibilité à tous les produits cochés, en une seule action (surtout pour les produits de la
 * Plateforme Socoda, que les fournisseurs ne livrent pas avec les codes de catégorie Ragues).
 */
export default function BulkActionBar({ productIds, count, onClear, onDone }: IProps) {
  const notificationToast = useNotification();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  async function apply(payload: { category_id?: string | null; visible?: boolean }, successMessage: string) {
    try {
      setLoading(true);
      const updated = await productsService.bulkUpdate(productIds, payload);
      notificationToast("Succès", `${successMessage} (${updated} produit${updated > 1 ? "s" : ""}).`, "success");
      await onDone();
    } catch (e: any) {
      notificationToast("Erreur", e.message, "error");
    } finally {
      setLoading(false);
    }
  }

  function handlePick(category: Category) {
    setPickerOpen(false);
    apply({ category_id: category.id }, `Catégorie « ${category.name} » affectée`);
  }

  return (
    <div className="flex flex-wrap items-center gap-3 mb-base p-3 bg-grey-5 border border-gray-20 rounded-rounded">
      <span className="inter-small-semibold">
        {count} produit{count > 1 ? "s" : ""} sélectionné{count > 1 ? "s" : ""}
      </span>
      <div className="flex flex-wrap gap-2 ml-auto">
        <Button variant="secondary" size="small" loading={loading} onClick={() => setPickerOpen(true)}>
          Affecter une catégorie
        </Button>
        <Button variant="secondary" size="small" loading={loading} onClick={() => apply({ visible: true }, "Produits rendus visibles")}>
          Rendre visibles
        </Button>
        <Button variant="secondary" size="small" loading={loading} onClick={() => apply({ visible: false }, "Produits masqués")}>
          Masquer
        </Button>
        <Button variant="ghost" size="small" onClick={onClear}>
          Tout désélectionner
        </Button>
      </div>

      {pickerOpen && (
        <CategoryPicker
          title="Affecter une catégorie aux produits sélectionnés"
          onClose={() => setPickerOpen(false)}
          onPick={handlePick}
        />
      )}
    </div>
  );
}
